class AnnouncementBar extends HTMLElement {
  constructor() {
    super();

    this.config = {
      autorotate: this.dataset.autorotate === 'true',
      autorotateSpeed: parseInt(this.dataset.autorotateSpeed) * 1000,
      moveTime: parseFloat(this.dataset.speed), // 100px going to move for
      space: 100, // 100px
    };

    if (theme.config.mqlSmall) {
      this.initOnMobile();
    }
    else {
      this.initOnDesktop();
    }

    document.addEventListener('matchSmall', () => {
      this.unload();
      this.initOnMobile();
    });

    document.addEventListener('unmatchSmall', () => {
      this.unload();
      this.initOnDesktop();
    });
  }

  initOnDesktop() {
    switch(this.dataset.layout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;

      default:
        this.initMarqueeOptimized();
    }
  }

  initOnMobile() {
    switch(this.dataset.mobileLayout) {
      case 'carousel':
        this.initCarousel();
        break;

      case 'marquee':
        this.initMarquee();
        break;
    }
  }

  unload() {
    if (this.flickity && typeof this.flickity.destroy === 'function') {
      this.flickity.destroy();
      this.removeNextPrevListeners();
    }

    if (this.classList.contains('marquee')) {
      this.classList.remove('marquee');
      this.querySelectorAll('.announcement-slider[aria-hidden]').forEach((slider) => {
        slider.remove();
      });
    }

    if (this.observer && typeof this.observer.unobserve === 'function') {
      this.observer.unobserve(this);
    }
  }

  initCarousel() {
    if (this.dataset.blockCount == 1) {
      return;
    }

    const slider = this.querySelector('.announcement-slider');
    setTimeout(() => {
      this.flickity = new Flickity(slider, {
        accessibility: false,
        rightToLeft: theme.config.rtl,
        prevNextButtons: false,
        pageDots: false,
        wrapAround: true,
        setGallerySize: false,
        autoPlay: this.config.autorotate ? this.config.autorotateSpeed : false,
      });
    });

    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.onNextPrevClick = this.handleNextPrevClick.bind(this);
    this.prevButton && this.prevButton.addEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.addEventListener('click', this.onNextPrevClick);
  }

  removeNextPrevListeners() {
    this.prevButton && this.prevButton.removeEventListener('click', this.onNextPrevClick);
    this.nextButton && this.nextButton.removeEventListener('click', this.onNextPrevClick);
  }

  handleNextPrevClick(event) {
    event.preventDefault();
    const target = event.currentTarget;

    if (target.name === 'next') {
      this.flickity && this.flickity.next();
    }
    else if (target.name === 'previous') {
      this.flickity && this.flickity.previous();
    }
  }

  initMarqueeOptimized() {
    setTimeout(() => {
      this.marqueeAnimation();
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isAnimated && this.classList.remove('marquee--paused');
        }
        else {
          this.isAnimated && this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);

    window.addEventListener('resize', this.marqueeAnimation.bind(this));
  }

  marqueeAnimation() {
    const slider = this.querySelector('.announcement-slider');

    if (slider.scrollWidth > this.clientWidth) {
      if (!this.isAnimated) {
        for (let index = 0; index < 2; index++) {
          const clone = slider.cloneNode(true);
          clone.setAttribute('aria-hidden', true);
          this.appendChild(clone);
        }

        this.classList.add('marquee');
        this.style.setProperty('--duration', `${(slider.scrollWidth / this.config.space) * this.config.moveTime}s`);
        this.isAnimated = true;
      }
    }
    else if (this.isAnimated) {
      for (let index = 0; index < 2; index++) {
        this.removeChild(this.lastElementChild);
      }
      this.classList.remove('marquee');
      this.isAnimated = false;
    }
  }

  initMarquee() {
    setTimeout(() => {
      const slider = this.querySelector('.announcement-slider');

      for (let index = 0; index < 10; index++) {
        const clone = slider.cloneNode(true);
        clone.setAttribute('aria-hidden', true);
        this.appendChild(clone);
      }

      this.classList.add('marquee');
      this.style.setProperty('--duration', `${(slider.clientWidth / this.config.space) * this.config.moveTime}s`);
    });

    // pause when out of view
    this.observer = new IntersectionObserver((entries, _observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.classList.remove('marquee--paused');
        }
        else {
          this.classList.add('marquee--paused');
        }
      });
    }, {rootMargin: '0px 0px 50px 0px'});

    this.observer.observe(this);
  }
}
customElements.define('announcement-bar', AnnouncementBar);

/* ============================================================
   무한 루프 가드 (셰프반찬 배송 위젯)
   원인: announcement-bar 섹션의 인라인 스크립트가 window.fetch를 후킹해서
        URL에 'cart.js'가 있으면 updateCart()를 예약하는데, updateCart() 자신이
        fetch('/cart.js')를 호출한다. 게다가 event-system 등 다른 스크립트의 cart.js
        읽기까지 이 후킹을 자극해서 cart.js 요청이 계속 발생(무한 루프/폭주).
   해결(1): cart.js '읽기' 요청을 인라인 후킹을 우회해 네이티브 fetch로 직행시킨다.
           → 인라인 후킹이 cart.js를 못 보므로 updateCart 재예약 사슬이 끊긴다.
           실제 장바구니 변경(/cart/add|change|update|clear)은 그대로 후킹을 거쳐 정상 갱신.
   해결(2, 보조): window.updateCart 자체도 leading-edge 디바운스로 한 번 더 방어.
   ============================================================ */
(function () {
  'use strict';
  if (window.__bcLoopGuard) return;
  window.__bcLoopGuard = true;

  // (1) cart.js 읽기 우회 — 네이티브 fetch를 이른 시점에 캡처(다른 래퍼보다 먼저 로드됨)
  var NATIVE_FETCH = (typeof window.fetch === 'function') ? window.fetch.bind(window) : null;
  function installOuter() {
    if (!NATIVE_FETCH) return;
    var current = window.fetch;
    if (current && current.__bcOuter) return;   // 이미 우리 래퍼가 최상위면 스킵
    var outer = function () {
      var a = arguments;
      var u = typeof a[0] === 'string' ? a[0] : (a[0] && a[0].url) || '';
      if (u.indexOf('cart.js') !== -1) {
        return NATIVE_FETCH.apply(window, a);    // cart.js 읽기 → 인라인 후킹 우회(네이티브 직행)
      }
      return current.apply(this, a);             // 그 외(변경 요청 포함)는 기존 체인 유지
    };
    outer.__bcOuter = true;
    window.fetch = outer;
  }
  installOuter();
  var fn = 0;
  var ivF = setInterval(function () {
    installOuter();                              // 인라인이 나중(비동기)에 다시 감싸도 우리를 최상위로 유지
    if (++fn > 60) clearInterval(ivF);           // 약 12초 동안 보장
  }, 200);

  // (2) 보조 방어 — window.updateCart leading-edge 디바운스
  var MIN_GAP = 1500, lastRun = 0, wrapped = false;
  function wrapUC() {
    if (wrapped) return;
    var orig = window.updateCart;
    if (typeof orig !== 'function') return;      // 인라인이 아직 정의 전 → 다음 폴링에서 재시도
    wrapped = true;
    window.updateCart = function () {
      var now = Date.now();
      if (now - lastRun < MIN_GAP) return;
      lastRun = now;
      return orig.apply(this, arguments);
    };
  }
  wrapUC();
  var uc = 0;
  var ivU = setInterval(function () {
    wrapUC();
    if (wrapped || ++uc > 200) clearInterval(ivU);
  }, 100);
})();
